// API Service for FTS Say Again Backend
// Use REACT_APP_API_URL if set, otherwise default to SayAgain service API
const baseUrl = process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai/api';
const API_BASE_URL = baseUrl.endsWith('/api') ? baseUrl.slice(0, -4) : baseUrl.replace(/\/api\/?$/, '');

// Dev proxy toggle:
// - If REACT_APP_USE_DEV_PROXY=true, we ALWAYS use CRA proxy in development (avoids CORS from localhost).
// - Otherwise, we use proxy only when REACT_APP_API_URL is NOT set.
const shouldUseDevProxy = () => {
  const forceProxy =
    (process.env.REACT_APP_USE_DEV_PROXY || '').toString().toLowerCase() === 'true';

  // If forceProxy is true, always allow it (avoids CORS issues on prod servers with local proxies)
  if (forceProxy) return true;

  if (
    process.env.NODE_ENV !== 'development' ||
    typeof window === 'undefined' ||
    !(
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    )
  ) {
    return false;
  }

  // If REACT_APP_API_URL is set (and not forcing proxy), prefer direct calls.
  return !process.env.REACT_APP_API_URL;
};

const buildRefreshUrl = () => {
  if (shouldUseDevProxy()) {
    return '/api/auth/refresh';
  }
  const cleanBaseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  return `${cleanBaseUrl}/api/auth/refresh`;
};

/** Clear tokens and cached auth/session data. Notifies listeners via `auth:session-expired`. */
export function clearAuthSession() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('chat_session_id');
  localStorage.removeItem('chat_session_project_id');
  localStorage.removeItem('chat_messages');
  localStorage.removeItem('signup_invite_params');
  sessionStorage.removeItem('user_fetched');
  sessionStorage.removeItem('avatars_fetched');
  sessionStorage.removeItem('has_avatars');
  sessionStorage.removeItem('user_id');
  sessionStorage.removeItem('user_data');
  sessionStorage.removeItem('avatars_data');
  sessionStorage.removeItem('plans_fetched');
  sessionStorage.removeItem('first_month_discount_active');
  sessionStorage.removeItem('discount_banner_dismissed');
  try {
    window.dispatchEvent(new CustomEvent('auth:session-expired'));
  } catch {
    // ignore (e.g. non-browser)
  }
}

/** Refresh access (and refresh) tokens from localStorage. Returns true on success. */
export async function refreshSessionTokens() {
  const storedRefresh = localStorage.getItem('refresh_token');
  if (!storedRefresh) return false;
  try {
    const refreshResp = await fetch(buildRefreshUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: storedRefresh }),
    });
    if (!refreshResp.ok) return false;
    const refreshData = await refreshResp.json();
    const newAccess = refreshData.access_token || refreshData.data?.access_token;
    const newRefresh = refreshData.refresh_token || refreshData.data?.refresh_token;
    if (!newAccess) return false;
    localStorage.setItem('access_token', newAccess);
    if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
    return true;
  } catch {
    return false;
  }
}

// Log API configuration on load (only in development)
if (process.env.NODE_ENV === 'development') {
  const willUseProxy = shouldUseDevProxy();
  console.log('API Configuration:', {
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    REACT_APP_USE_DEV_PROXY: process.env.REACT_APP_USE_DEV_PROXY,
    API_BASE_URL: API_BASE_URL,
    willUseProxy: willUseProxy,
    proxyTarget: willUseProxy ? 'CRA proxy (package.json)' : API_BASE_URL,
    FullURL: willUseProxy ? '/api/auth/register' : `${API_BASE_URL}/api/auth/register`
  });

  if (process.env.REACT_APP_API_URL && !willUseProxy) {
    console.warn('⚠️ CORS Warning: Direct API calls may fail. Set REACT_APP_USE_DEV_PROXY=true in .env to use proxy.');
  }
}

// Helper function for API calls
const apiRequest = async (endpoint, options = {}) => {
  // Ensure endpoint starts with /
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

  // Determine URL based on environment configuration
  // Priority: REACT_APP_API_URL > proxy (if no env var) > default SayAgain service
  let url;
  const shouldUseProxy = shouldUseDevProxy();

  if (shouldUseProxy) {
    // Use relative URL to leverage proxy (only when REACT_APP_API_URL is not set)
    url = cleanEndpoint;
  } else {
    // Use absolute URL from environment variable or default SayAgain service API
    const cleanBaseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    url = `${cleanBaseUrl}${cleanEndpoint}`;
  }

  // Log URL in development
  if (process.env.NODE_ENV === 'development') {
    console.log('API Request:', {
      endpoint,
      url,
      API_BASE_URL,
      REACT_APP_API_URL: process.env.REACT_APP_API_URL,
      usingProxy: shouldUseProxy,
      method: options.method || 'GET',
      hasBody: !!options.body
    });
  }

  const defaultHeaders = {};

  // Only add Content-Type for plain JSON requests if meaningful (non-GET/HEAD)
  // GET requests with Content-Type can cause 422 errors on strict backends (e.g. FastAPI)
  const method = (options.method || 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  // Add auth token if available
  const token = localStorage.getItem('access_token');
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    // Handle non-JSON responses
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(text || `HTTP ${response.status}: ${response.statusText}`);
      }
    }

    if (!response.ok) {
      // Create an error object that includes status code
      let errorMessage;
      // Word substitutions for 422 messages were meant for login/register only; applying them to
      // payment and other APIs turns generic "field required" into misleading "Password is required".
      const isAuthEndpoint = typeof cleanEndpoint === 'string' && cleanEndpoint.includes('/api/auth/');

      // Handle specific status codes with user-friendly messages
      if (response.status === 429) {
        errorMessage = data.msg || data.error || data.message || 'Too many requests. Please wait a moment and try again.';
      } else if (response.status === 401) {
        // Attempt a one-time token refresh before giving up
        if (!isAuthEndpoint && !options._isRetry) {
          const refreshed = await refreshSessionTokens();
          if (refreshed) {
            const newToken = localStorage.getItem('access_token');
            if (newToken) {
              return apiRequest(endpoint, {
                ...options,
                _isRetry: true,
                headers: {
                  ...options.headers,
                  Authorization: `Bearer ${newToken}`,
                },
              });
            }
          }
          // Refresh failed (or no refresh token) — clear stale session so UI shows Sign In
          clearAuthSession();
        } else if (!isAuthEndpoint && options._isRetry) {
          // Retry after refresh still unauthorized
          clearAuthSession();
        }
        errorMessage = data.msg || data.error || data.message || 'Your session has expired. Please sign in again.';
      } else if (response.status === 403) {
        errorMessage = data.msg || data.error || data.message || 'Access forbidden. You don\'t have permission to perform this action. Please make sure you are signed in.';
      } else if (response.status === 422) {
        // Unprocessable Entity - validation errors
        errorMessage = data.msg || data.error || data.message || 'Validation failed. Please check your input.';
        // If there are validation details, include them
        if (data.detail && Array.isArray(data.detail)) {
          const validationErrors = data.detail.map((err) => {
            let msg = err.msg || err.message || '';
            if (
              isAuthEndpoint &&
              (msg.toLowerCase().includes('string') || msg.toLowerCase().includes('field'))
            ) {
              msg = msg.replace(/string/gi, 'Password').replace(/field/gi, 'Password');
            }
            return msg || `${err.loc?.join('.')}: ${err.msg || err.message}`;
          }).join(', ');
          if (validationErrors) {
            errorMessage = validationErrors;
          }
        } else if (data.detail && typeof data.detail === 'string') {
          errorMessage = isAuthEndpoint
            ? data.detail.replace(/string/gi, 'Password')
            : data.detail;
        }
      } else if (response.status === 400) {
        errorMessage =
          data.msg ||
          data.error ||
          data.message ||
          (isAuthEndpoint ? 'Invalid request. Please check your password.' : 'Invalid request. Please check your input.');
      } else if (response.status >= 500) {
        errorMessage = data.msg || data.error || data.message || 'Server error. Please try again later.';
      } else {
        errorMessage = data.msg || data.error || data.message || `Request failed. Please try again.`;
      }

      const apiError = new Error(errorMessage);
      apiError.status = response.status;
      apiError.statusCode = response.status;
      apiError.response = data;
      throw apiError;
    }

    return data;
  } catch (error) {
    // Handle network errors (CORS, connection refused, SSL errors, etc.)
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error('API Request Failed:', {
        url,
        baseUrl: API_BASE_URL,
        endpoint,
        error: error.message,
        errorName: error.name,
        origin: window.location.origin,
        REACT_APP_API_URL: process.env.REACT_APP_API_URL
      });

      // Check for CORS errors specifically
      if (error.message.includes('CORS') || error.message.includes('Failed to fetch') || error.message.includes('network')) {
        // Check if it's a local development URL
        if (url.includes('192.168.') || url.includes('localhost') || url.includes('127.0.0.1')) {
          throw new Error(`Unable to connect to API server at ${url}.\n\nPlease check:\n1. The API server is running at ${API_BASE_URL}\n2. The server is accessible from your network\n3. CORS is enabled on the backend API\n4. Firewall is not blocking the connection`);
        }
        throw new Error(`CORS Error: The API server at ${API_BASE_URL} is not allowing requests from ${window.location.origin}.\n\nPlease ensure:\n1. CORS is enabled on the backend API\n2. The backend allows requests from: ${window.location.origin}\n3. The backend includes proper CORS headers (Access-Control-Allow-Origin)`);
      }

      // Check for SSL errors specifically
      if (error.message.includes('SSL') || error.message.includes('ERR_SSL')) {
        throw new Error(`SSL Error: Unable to connect securely to ${API_BASE_URL}. Please check:\n1. The API URL is correct\n2. The API server has valid SSL certificates\n3. Try accessing the API directly in your browser`);
      }

      // Generic network error with helpful message
      if (url.includes('192.168.') || url.includes('localhost') || url.includes('127.0.0.1')) {
        throw new Error(`Network Error: Unable to connect to API server at ${url}.\n\nPlease check:\n1. The API server is running\n2. The server is accessible from your network\n3. Check your network connection\n4. Verify the API URL in your environment variables`);
      }

      throw new Error(`Failed to fetch: ${error.message}. Please check:\n1. API server is accessible at ${API_BASE_URL}\n2. CORS is enabled on the API server\n3. Network connection is active`);
    }
    // Re-throw other errors
    throw error;
  }
};

// Auth APIs
export const authAPI = {
  register: (userData) => apiRequest('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify(userData),
  }),

  login: (credentials) => {
    // Add flag: 'local' for local login if not provided
    const loginData = {
      ...credentials,
      flag: credentials.flag || 'local'
    };

    // Log login request in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Login Request:', {
        email: loginData.email,
        flag: loginData.flag,
        hasPassword: !!loginData.password,
        payload: loginData
      });
    }

    return apiRequest('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json',
      },
      body: JSON.stringify(loginData),
    });
  },

  refreshToken: (refreshToken) => apiRequest('/api/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refresh_token: refreshToken }),
  }),

  getCurrentUser: () => apiRequest('/api/auth/me', {
    method: 'GET',
  }),

  forgotPassword: (emailData) => apiRequest('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify(emailData),
  }),

  resetPassword: ({ token, password }) => apiRequest('/api/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  }),

  // Google Sign-In (using login endpoint with flag: 'google')
  // Sends the raw Google ID token; the backend verifies it and derives identity.
  googleSignin: (googleData) => apiRequest('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({
      credential: googleData.credential,
      flag: 'google',
      ...(googleData.invite_id ? { invite_id: googleData.invite_id } : {}),
    }),
  }),

  // Google Sign-Up (using register endpoint as per user requirement)
  googleSignup: (googleData) => apiRequest('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify(googleData),
  }),
};

// Plans APIs
export const plansAPI = {
  getAllPlans: () => apiRequest('/api/plans/all', {
    method: 'GET',
  }),

  getPlans: (page = 1, pageSize = 10) => apiRequest(`/api/plans?page=${page}&page_size=${pageSize}`, {
    method: 'GET',
  }),

  // Get plan details from admin endpoint
  getPlanDetails: () => apiRequest('/api/admin/plans/details', {
    method: 'GET',
    headers: {
      'accept': 'application/json',
    },
  }),

  // Get avatar plans (authenticated; invite-aware)
  getAvatarPlans: () => apiRequest('/api/plans/avatar-plans', {
    method: 'GET',
  }),

  // Public catalog — flat interval rows (no auth); fallback for invite gift picker
  getAvatarPlansPublic: () => apiRequest('/api/avatar-plans', {
    method: 'GET',
  }),

  // Get all addons
  getAddons: () => apiRequest('/api/addons', {
    method: 'GET',
  }),

  // Get user token balance
  getTokenBalance: () => apiRequest('/api/addons/token-balance', {
    method: 'GET',
    headers: {
      'accept': 'application/json',
    },
  }),

  // Create PayPal order for addon
  createAddonPayPalOrder: (payload) => {
    const body = typeof payload === 'object' ? payload : { addon_plan_id: payload };
    return apiRequest('/api/addons/paypal/create-order', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // Capture PayPal payment for addon
  captureAddonPayPalOrder: (data) => apiRequest('/api/addons/paypal/capture', {
    method: 'POST',
    body: JSON.stringify(data), // { order_id, addon_plan_id }
  }),

  // // Payment flow plan source
  // getAvatarPlansForCheckout: () => apiRequest('/api/avatar-plans', {
  //   method: 'GET',
  // }),
};

// Users APIs
export const usersAPI = {
  getUsers: (page = 1, pageSize = 10) => apiRequest(`/api/users?page=${page}&page_size=${pageSize}`, {
    method: 'GET',
  }),

  getUser: (id) => apiRequest(`/api/users/${id}`, {
    method: 'GET',
  }),

  createUser: (userData) => apiRequest('/api/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  }),

  updateUser: (id, userData) => apiRequest(`/api/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(userData),
  }),

  deleteUser: (id) => apiRequest(`/api/users/${id}`, {
    method: 'DELETE',
  }),

  // Update user profile (first_name and last_name only)
  updateUserProfile: (profileData) => apiRequest('/api/users/profile', {
    method: 'PUT',
    body: JSON.stringify(profileData),
  }),

  // Download account backup as ZIP
  exportBackup: async () => {
    const endpoint = '/api/users/me/export';
    const shouldUseProxy = shouldUseDevProxy();
    const url = shouldUseProxy ? endpoint : `${API_BASE_URL.replace(/\/$/, '')}${endpoint}`;

    const token = localStorage.getItem('access_token');
    const headers = {
      'Accept': 'application/zip',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      let errorMsg = 'Failed to download backup';
      try {
        const data = await response.json();
        errorMsg = data.msg || data.error || data.message || errorMsg;
      } catch (e) {
        const text = await response.text();
        if (text) errorMsg = text;
      }
      throw new Error(errorMsg);
    }

    return await response.blob();
  },
};

// Avatars APIs
export const avatarsAPI = {
  // Create avatar with file uploads (multipart/form-data)
  // Matches cURL structure: 
  // -F "first_name=..." -F "last_name=..." -F "user_id=..." -F "avatar_type_id=..."
  // -F "plan_interval=..." (if for loved one)
  // -F "selected_email_recipients=email1" -F "selected_email_recipients=email2" (multiple emails with same field name)
  // -F "files=@file1" -F "files=@file2" -F "files=@file3" (multiple files with same field name)
  createAvatar: async (avatarData, files = [], selectedEmailRecipients = []) => {
    const cleanEndpoint = '/api/avatars';

    // Use environment-based URL configuration
    let url;
    const shouldUseProxy = shouldUseDevProxy();
    if (shouldUseProxy) {
      // Use relative URL to leverage proxy (only when REACT_APP_API_URL is not set)
      url = cleanEndpoint;
    } else {
      // Use absolute URL from environment variable or default SayAgain service API
      const cleanBaseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      url = `${cleanBaseUrl}${cleanEndpoint}`;
    }

    const formData = new FormData();
    formData.append('first_name', avatarData.first_name);
    // Only append last_name if it's a real value — undefined becomes the string "undefined" in FormData
    if (avatarData.last_name !== undefined && avatarData.last_name !== null) {
      formData.append('last_name', avatarData.last_name);
    }
    // Only append avatar_type_id if it's a real integer — undefined/null become "undefined"/"null" in FormData,
    // which Pydantic cannot parse as int and throws "Input should be a valid integer"
    if (avatarData.avatar_type_id !== undefined && avatarData.avatar_type_id !== null) {
      formData.append('avatar_type_id', avatarData.avatar_type_id);
    }
    formData.append('user_id', avatarData.user_id);
    formData.append('relation', avatarData.relation === null ? '' : (avatarData.relation || ''));

    // Add avatar_type — used as fallback when avatar_type_id is not available
    formData.append('avatar_type', avatarData.avatar_type || '');

    // Add plan_id (backend expects it; default to 1 if not provided)
    const planId =
      avatarData?.plan_id !== undefined && avatarData?.plan_id !== null && `${avatarData.plan_id}`.trim() !== ''
        ? avatarData.plan_id
        : 1;
    formData.append('plan_id', planId);

    // Add invite_id if provided (user came via invite link from signup)
    if (avatarData.invite_id) {
      formData.append('invite_id', avatarData.invite_id);
    }

    // Add plan_interval if provided (dynamic from selected plan)
    if (avatarData.plan_interval) {
      formData.append('plan_interval', avatarData.plan_interval);
      console.log('api.js: Added plan_interval to FormData:', avatarData.plan_interval);
    }

    // Append selected_email_recipients - empty string if no emails (matches new curl format)
    // Matches cURL: -F "selected_email_recipients=" or -F "selected_email_recipients=email1@example.com"
    if (selectedEmailRecipients && selectedEmailRecipients.length > 0) {
      selectedEmailRecipients.forEach((email) => {
        formData.append('selected_email_recipients', email);
      });
    } else {
      // Append empty string if no emails (matches curl format)
      formData.append('selected_email_recipients', '');
    }

    // Append multiple files with the same field name 'files' (matches cURL: -F "files=@file1" -F "files=@file2")
    if (files && files.length > 0) {
      files.forEach((file) => {
        // Validate that file is a valid File object
        if (file instanceof File || file instanceof Blob) {
          formData.append('files', file);
        } else {
          console.warn('Invalid file object:', file);
        }
      });
    }

    const headers = {};
    const token = localStorage.getItem('access_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    headers['accept'] = 'application/json';
    // Note: Don't set Content-Type for FormData, browser will set it with boundary

    // Log form data in development
    if (process.env.NODE_ENV === 'development') {
      const totalFileSize = files.reduce((total, file) => total + (file?.size || 0), 0);
      console.log('Creating avatar with:', {
        url,
        first_name: avatarData.first_name,
        last_name: avatarData.last_name,
        avatar_type_id: avatarData.avatar_type_id,
        user_id: avatarData.user_id,
        plan_id: avatarData.plan_id,
        plan_interval: avatarData.plan_interval,
        selected_email_recipients: selectedEmailRecipients,
        files_count: files.length,
        total_file_size: `${(totalFileSize / (1024 * 1024)).toFixed(2)} MB`,
        file_names: files.map(f => f?.name || 'unknown')
      });
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      // Handle non-JSON responses
      let data;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          const text = await response.text();
          if (text) {
            try {
              data = JSON.parse(text);
            } catch {
              // If not JSON, create a simple error object
              data = { error: text || `HTTP ${response.status}: ${response.statusText}` };
            }
          } else {
            data = { error: `HTTP ${response.status}: ${response.statusText}` };
          }
        }
      } catch (parseError) {
        // If we can't parse the response, create a basic error object
        console.error('Error parsing response:', parseError);
        data = { error: `HTTP ${response.status}: ${response.statusText}` };
      }

      if (!response.ok) {
        // Create an error object that includes status code
        // Check for nested error object structure: { error: { msg: "...", error: "..." } }
        let errorMessage = data.error?.msg || data.error?.error || data.msg || data.error || data.message || 'Failed to create avatar';

        // Handle specific status codes with user-friendly messages
        if (response.status === 400) {
          // Check for nested error structure first
          errorMessage = data.error?.msg || data.error?.error || data.msg || data.error || data.message || 'Invalid request data. Please check your input and try again.';
        } else if (response.status === 401) {
          errorMessage = data.msg || data.error || data.message || 'Unauthorized. Please sign in again.';
        } else if (response.status === 403) {
          errorMessage = data.msg || data.error || data.message || 'Access forbidden. You don\'t have permission to perform this action.';
        } else if (response.status === 404) {
          errorMessage = data.msg || data.error || data.message || 'Avatar endpoint not found. Please check the API configuration.';
        } else if (response.status === 422) {
          // Unprocessable Entity - validation errors
          errorMessage = data.msg || data.error || data.message || 'Validation error. Please check your input.';
          if (data.detail && Array.isArray(data.detail)) {
            const validationErrors = data.detail.map(err => err.msg || err.message || '').filter(Boolean).join(', ');
            if (validationErrors) {
              errorMessage = validationErrors;
            }
          } else if (data.detail && typeof data.detail === 'string') {
            errorMessage = data.detail;
          }
        } else if (response.status === 429) {
          errorMessage = data.msg || data.error || data.message || 'Too many requests. Please wait a moment and try again.';
        } else if (response.status >= 500) {
          errorMessage = data.msg || data.error || data.message || `Server error (${response.status}). Please try again later or contact support if the problem persists.`;
        }

        const apiError = new Error(errorMessage);
        apiError.status = response.status;
        apiError.statusCode = response.status;
        apiError.response = data;
        throw apiError;
      }

      // Log successful response in development
      if (process.env.NODE_ENV === 'development') {
        console.log('Avatar creation API response:', {
          status: data.status,
          status_code: data.status_code,
          has_data: !!data.data,
          avatar_id: data.data?.id,
          uploaded_files_count: data.data?.uploaded_files?.length || 0
        });
      }

      // Return the response as-is to preserve the structure: { status: true, status_code: 201, data: {...} }
      return data;
    } catch (error) {
      // If error already has a status code, it's an API error (not a network error)
      if (error.status || error.statusCode) {
        // This is an API error (400, 500, etc.), not a network/CORS error
        console.error('Avatar Creation API Error:', {
          status: error.status || error.statusCode,
          message: error.message,
          response: error.response,
          url
        });
        throw error;
      }

      // Handle network errors (CORS, connection refused, SSL errors, etc.)
      // Only catch actual network errors (TypeError from fetch)
      if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
        console.error('Avatar Creation API Request Failed (Network Error):', {
          url,
          baseUrl: API_BASE_URL,
          endpoint: cleanEndpoint,
          error: error.message,
          errorName: error.name,
          origin: window.location.origin,
          REACT_APP_API_URL: process.env.REACT_APP_API_URL,
          usingProxy: shouldUseDevProxy()
        });

        // Determine the actual API URL being used for error message
        const actualApiUrl = shouldUseDevProxy()
          ? 'proxy (see package.json proxy)'
          : API_BASE_URL;

        // Check for CORS errors specifically
        if (error.message.includes('CORS') || error.message.toLowerCase().includes('cors')) {
          throw new Error(`CORS Error: The API server is not allowing requests from ${window.location.origin}.\n\nPlease ensure:\n1. CORS is enabled on the backend API\n2. The backend allows requests from: ${window.location.origin}\n3. The backend includes proper CORS headers (Access-Control-Allow-Origin)`);
        }

        // Check for SSL errors specifically
        if (error.message.includes('SSL') || error.message.includes('ERR_SSL')) {
          throw new Error(`SSL Error: Unable to connect securely to the API server. Please check:\n1. The API URL is correct\n2. The API server has valid SSL certificates\n3. Try accessing the API directly in your browser`);
        }

        // Generic network error with context-aware message
        if (shouldUseDevProxy()) {
          throw new Error(`Network Error: Unable to connect to the API server via proxy.\n\nPlease check:\n1. The proxy server target in package.json is reachable\n2. The backend API is accessible\n3. Network connection is active\n4. Check browser console for more details`);
        } else {
          throw new Error(`Network Error: Unable to connect to the API server at ${API_BASE_URL}.\n\nPlease check:\n1. API server is accessible at ${API_BASE_URL}\n2. CORS is enabled on the API server\n3. Network connection is active\n4. The API server is running`);
        }
      }

      // Re-throw other errors (including API errors with status codes)
      throw error;
    }
  },

  // // Get all avatars for a user
  // getAvatars: (userId) => apiRequest(`/api/avatars?user_id=${userId}`, {
  //   method: 'GET',
  // }),

  // Get single avatar
  getAvatar: (id) => apiRequest(`/api/avatars/${id}`, {
    method: 'GET',
  }),

  // Get avatar prediction (overall confidence, knowledge sources, topics)
  getAvatarPrediction: (avatarId) => apiRequest(`/api/avatars/${avatarId}/prediction`, {
    method: 'GET',
  }),

  // Improve avatar memory - POST /api/avatars/{id}/improve-memory
  improveMemory: (avatarId, memory) => apiRequest(`/api/avatars/${avatarId}/improve-memory`, {
    method: 'POST',
    body: JSON.stringify({ memory }),
  }),

  // Update avatar
  updateAvatar: (id, avatarData) => apiRequest(`/api/avatars/${id}`, {
    method: 'PUT',
    body: JSON.stringify(avatarData),
  }),

  // Update avatar profile (first_name, last_name, or auto_renew)
  updateAvatarProfile: (id, profileData) => apiRequest(`/api/avatars/${id}/profile`, {
    method: 'PUT',
    body: JSON.stringify(profileData),
  }),

  // Cancel avatar subscription
  cancelSubscription: () => apiRequest('/api/avatars/cancel-subscription', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    }
  }),

  // Delete avatar
  deleteAvatar: (id) => apiRequest(`/api/avatars/${id}`, {
    method: 'DELETE',
  }),

  // Extract emails from uploaded files
  // Matches cURL: 
  // curl -X 'POST' 'https://sayagainservices.sayagain.ai/api/avatars/extract-emails' \
  //   -H 'accept: application/json' \
  //   -H 'Content-Type: multipart/form-data' \
  //   -F 'user_id=109' \
  //   -F 'files=@file1' -F 'files=@file2'
  extractEmails: async (files = [], userId, personalFilter = false) => {
    const cleanEndpoint = '/api/avatars/extract-emails';

    // Use environment-based URL configuration
    let url;
    const shouldUseProxy = shouldUseDevProxy();
    if (shouldUseProxy) {
      // Use relative URL to leverage proxy (only when REACT_APP_API_URL is not set)
      url = cleanEndpoint;
    } else {
      // Use absolute URL from environment variable or default SayAgain service API
      const cleanBaseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      url = `${cleanBaseUrl}${cleanEndpoint}`;
    }

    const formData = new FormData();

    // Append user_id (required by API)
    // Matches cURL: -F 'user_id=109'
    if (userId) {
      formData.append('user_id', userId);
    }

    if (personalFilter) {
      formData.append('personal_filter', 'true');
    }

    // Append multiple files with the same field name 'files'
    // Matches cURL: -F 'files=@file1' -F 'files=@file2'
    if (files && files.length > 0) {
      files.forEach((file) => {
        // Validate that file is a valid File object
        if (file instanceof File || file instanceof Blob) {
          formData.append('files', file);
        } else {
          console.warn('Invalid file object:', file);
        }
      });
    }

    const headers = {
      'accept': 'application/json',
    };

    const token = localStorage.getItem('access_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Note: Don't set Content-Type for FormData manually
    // Browser will automatically set it with boundary: 'multipart/form-data; boundary=...'
    // Setting it manually would break the boundary parameter

    // Log form data in development
    if (process.env.NODE_ENV === 'development') {
      const totalFileSize = files.reduce((total, file) => total + (file?.size || 0), 0);
      console.log('Extracting emails with:', {
        url,
        user_id: userId,
        files_count: files.length,
        total_file_size: `${(totalFileSize / (1024 * 1024)).toFixed(2)} MB`,
        file_names: files.map(f => f?.name || 'unknown')
      });
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      // Handle non-JSON responses
      let data;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          const text = await response.text();
          if (text) {
            try {
              data = JSON.parse(text);
            } catch {
              data = { error: text || `HTTP ${response.status}: ${response.statusText}` };
            }
          } else {
            data = { error: `HTTP ${response.status}: ${response.statusText}` };
          }
        }
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        data = { error: `HTTP ${response.status}: ${response.statusText}` };
      }

      // Log response in development
      if (process.env.NODE_ENV === 'development') {
        console.log('Email extraction API response:', {
          status: response.status,
          ok: response.ok,
          has_data: !!data,
          data: data
        });
      }

      if (!response.ok) {
        let errorMessage = data.msg || data.error || data.message || 'Failed to extract emails';

        // Handle specific status codes
        if (response.status === 400) {
          errorMessage = data.msg || data.error || data.message || 'Invalid request data. Please check your input.';
        } else if (response.status === 401) {
          errorMessage = data.msg || data.error || data.message || 'Unauthorized. Please sign in again.';
        } else if (response.status === 404) {
          errorMessage = data.msg || data.error || data.message || 'Email extraction endpoint not found.';
        } else if (response.status >= 500) {
          errorMessage = data.msg || data.error || data.message || `Server error (${response.status}). Please try again later.`;
        }

        const apiError = new Error(errorMessage);
        apiError.status = response.status;
        apiError.statusCode = response.status;
        apiError.response = data;
        throw apiError;
      }

      return data;
    } catch (error) {
      // If error already has a status code, it's an API error
      if (error.status || error.statusCode) {
        throw error;
      }

      // Handle network errors (CORS, connection refused, SSL errors, etc.)
      // Only catch actual network errors (TypeError from fetch)
      if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
        console.error('Email Extraction API Request Failed (Network Error):', {
          url,
          baseUrl: API_BASE_URL,
          endpoint: cleanEndpoint,
          error: error.message,
          errorName: error.name,
          origin: window.location.origin,
          REACT_APP_API_URL: process.env.REACT_APP_API_URL,
          usingProxy: shouldUseProxy,
          filesCount: files.length,
          totalFileSize: files.reduce((total, file) => total + (file?.size || 0), 0)
        });

        // Determine the actual API URL being used for error message
        const actualApiUrl = shouldUseProxy
          ? 'proxy (see package.json proxy)'
          : API_BASE_URL;

        // Check for CORS errors specifically
        if (error.message.includes('CORS') || error.message.toLowerCase().includes('cors')) {
          throw new Error(`CORS Error: The API server is not allowing requests from ${window.location.origin}.\n\nPlease ensure:\n1. CORS is enabled on the backend API\n2. The backend allows requests from: ${window.location.origin}\n3. The backend includes proper CORS headers (Access-Control-Allow-Origin)\n4. If in development, set REACT_APP_USE_DEV_PROXY=true in .env to use proxy`);
        }

        // Check for SSL errors specifically
        if (error.message.includes('SSL') || error.message.includes('ERR_SSL')) {
          throw new Error(`SSL Error: Unable to connect securely to the API server. Please check:\n1. The API URL is correct\n2. The API server has valid SSL certificates\n3. Try accessing the API directly in your browser`);
        }

        // Generic network error with context-aware message
        if (shouldUseProxy) {
          throw new Error(`Network Error: Unable to connect to the API server via proxy.\n\nPlease check:\n1. The proxy server target in package.json is reachable\n2. The backend API is accessible\n3. Network connection is active\n4. Check browser console for more details`);
        } else {
          throw new Error(`Network Error: Unable to connect to the API server at ${API_BASE_URL}.\n\nPlease check:\n1. API server is accessible at ${API_BASE_URL}\n2. CORS is enabled on the API server\n3. Network connection is active\n4. The API server is running\n5. If in development, set REACT_APP_USE_DEV_PROXY=true in .env to use proxy`);
        }
      }

      throw error;
    }
  },

  // Get all avatars for a user
  getAvatars: (userId) => apiRequest(`/api/avatars?user_id=${userId}`, {
    method: 'GET',
  }),

  // Get my avatar details
  getMyDetails: () => apiRequest('/api/avatars/my-details', {
    method: 'GET',
  }),

  exportAvatarData: async (avatarId) => {
    const token = localStorage.getItem('access_token');
    const headers = { 'Accept': 'text/plain' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${API_BASE_URL}/api/avatars/${avatarId}/export`, { method: 'GET', headers });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      const errMsg = err?.msg || err?.message || (typeof err?.error === 'string' ? err.error : null);
      throw new Error(errMsg || 'Export failed');
    }
    return response.text();
  },

  // Member relation capture ------------------------------------------------

  // Get the list of supported avatar relation types for the dropdown.
  getRelations: () => apiRequest('/api/avatars/relations', { method: 'GET' }),

  // Get the current user's relation status for a specific avatar.
  // Returns: { avatar_name, relation, dismiss_count, needs_capture }
  getMemberRelation: (avatarId) => apiRequest(`/api/avatars/${avatarId}/member-relation`, {
    method: 'GET',
  }),

  // Get all users who have access to this avatar (owner + AvatarMemberRelation rows)
  getAvatarMembers: (avatarId) => apiRequest(`/api/avatars/${avatarId}/members`, {
    method: 'GET',
  }),

  // Store the selected relation for an invited member.
  setMemberRelation: (avatarId, relation) => apiRequest(`/api/avatars/${avatarId}/member-relation`, {
    method: 'POST',
    body: JSON.stringify({ relation }),
  }),

  // Record a dismissal (no relation selected). After 2 dismissals the backend
  // sets relation to "unknown" and the screen will no longer appear.
  dismissMemberRelation: (avatarId) => apiRequest(`/api/avatars/${avatarId}/member-relation`, {
    method: 'POST',
    body: JSON.stringify({ dismiss: true }),
  }),

  // Improve modal -------------------------------------------------------

  // Load improve-modal context: confidence score, stats, and first question.
  getImproveContext: (avatarId) => apiRequest(`/api/avatars/${avatarId}/improve-context`, {
    method: 'GET',
  }),

  // Submit the user's answer to a question; returns acknowledgment + next question.
  submitImproveAnswer: (avatarId, answer, question, sessionHistory, coveredDomains) => apiRequest(`/api/avatars/${avatarId}/improve-answer`, {
    method: 'POST',
    body: JSON.stringify({
      answer,
      question: question || '',
      session_history: sessionHistory || [],
      covered_domains: coveredDomains || [],
    }),
  }),

  // Upload additional documents via the improve modal (owner only).
  addAvatarDocuments: async (avatarId, files) => {
    // Generate URL using same logic as other endpoints
    const cleanEndpoint = `/api/avatars/${avatarId}/add-documents`;
    const cleanBaseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    const url = `${cleanBaseUrl}${cleanEndpoint}`;

    const doFetch = (token) => {
      const formData = new FormData();
      // Ensure we only append valid File/Blob objects
      files.forEach((file) => {
        if (file instanceof File || file instanceof Blob) {
          formData.append('files', file);
        } else {
          console.warn('addAvatarDocuments: Skipping invalid file object:', file);
        }
      });
      
      const headers = {
        'Accept': 'application/json'
      };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      return fetch(url, { method: 'POST', headers, body: formData });
    };

    let response = await doFetch(localStorage.getItem('access_token'));

    // On 401, attempt a one-time token refresh and retry
    if (response.status === 401) {
      const storedRefresh = localStorage.getItem('refresh_token');
      if (storedRefresh) {
        try {
          const refreshUrl = `${cleanBaseUrl}/api/auth/refresh`;
          const refreshResp = await fetch(refreshUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: storedRefresh }),
          });
          if (refreshResp.ok) {
            const refreshData = await refreshResp.json();
            const newToken = refreshData.access_token || refreshData.data?.access_token;
            if (newToken) {
              localStorage.setItem('access_token', newToken);
              response = await doFetch(newToken);
            }
          }
        } catch (_) { /* fall through to error handling below */ }
      }
    }

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err?.msg || err?.message || err?.error || `Upload failed (${response.status})`);
    }
    return response.json();
  },

  // Send invite emails to selected recipients after avatar creation
  // POST /api/avatars/<AVATAR_ID>/invite-emails
  // Send invitation emails AFTER avatar creation
  // Flow: create avatar -> response includes extracted_emails -> frontend selects emails -> call this endpoint
  // This endpoint ONLY accepts emails that were extracted from the avatar creation (extracted_emails)
  // 
  // cURL format:
  // curl -X POST "http://192.168.1.5:8090/api/avatars/{avatar_id}/invite-emails" \
  //   -H "Content-Type: application/json" \
  //   -H "Authorization: Bearer <ACCESS_TOKEN>" \
  //   -d '{
  //     "selected_email_recipients": [
  //       {
  //         "email": "nidhisakharkar99@gmail.com",
  //         "plan_id": 2,
  //         "plan_pricing_id": 5
  //       }
  //     ]
  //   }'
  //
  // Backend contract:
  // { selected_email_recipients: Array<{ email: string, plan_id: number, plan_pricing_id?: number }> }
  // Also accepts legacy callers:
  // - sendInviteEmails(avatarId, ['a@test.com']) -> coerced to objects with default plan_id=1
  sendInviteEmails: async (avatarId, payloadOrEmails = []) => {
    const cleanEndpoint = `/api/avatars/${avatarId}/invite-emails`;

    // Use environment-based URL configuration
    let url;
    const shouldUseProxy = shouldUseDevProxy();
    if (shouldUseProxy) {
      // Use relative URL to leverage proxy (only when REACT_APP_API_URL is not set)
      url = cleanEndpoint;
    } else {
      // Use absolute URL from environment variable or default SayAgain service API
      const cleanBaseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      url = `${cleanBaseUrl}${cleanEndpoint}`;
    }

    const headers = {
      accept: 'application/json',
      'Content-Type': 'application/json',
    };

    // Get and validate access token
    const token = localStorage.getItem('access_token');
    if (!token || !token.trim()) {
      const authErr = new Error('Unauthorized: missing access token. Please sign in again.');
      authErr.status = 401;
      authErr.statusCode = 401;
      throw authErr;
    }

    // Trim token to remove any whitespace and set Authorization header
    const cleanToken = token.trim();
    headers['Authorization'] = `Bearer ${cleanToken}`;

    // Log token info in development (without exposing full token)
    if (process.env.NODE_ENV === 'development') {
      console.log('Invite emails auth:', {
        hasToken: !!token,
        tokenLength: cleanToken.length,
        tokenPrefix: cleanToken.substring(0, 20) + '...',
        authHeader: `Bearer ${cleanToken.substring(0, 20)}...`
      });
    }

    // Normalize payload to match backend contract:
    // { selected_email_recipients: Array<{ email: string, plan_id: number }> }
    const payloadRaw =
      Array.isArray(payloadOrEmails)
        ? { selected_email_recipients: payloadOrEmails }
        : (payloadOrEmails || {});

    const selectedRaw = Array.isArray(payloadRaw.selected_email_recipients)
      ? payloadRaw.selected_email_recipients
      : [];

    // Coerce into Array<{email, plan_id}>
    const normalized = selectedRaw
      .map((i) => {
        // Legacy: allow raw string email entries
        if (typeof i === 'string') {
          const email = i.trim();
          return email ? { email, plan_id: 1 } : null;
        }

        // Current: object entries
        if (i && typeof i === 'object') {
          const email = typeof i.email === 'string' ? i.email.trim() : '';
          if (!email) return null;

          // plan_id is REQUIRED for every email (backend validation)
          let planId;
          if (i.plan_id !== null && i.plan_id !== undefined) {
            // plan_id is provided - convert to number
            if (Number.isFinite(i.plan_id)) {
              planId = Number(i.plan_id);
            } else if (typeof i.plan_id === 'string' && i.plan_id.trim() !== '' && !Number.isNaN(Number(i.plan_id))) {
              planId = Number(i.plan_id);
            } else {
              // Invalid plan_id format, default to 1
              console.warn(`Invalid plan_id format for ${email}:`, i.plan_id, 'Defaulting to 1');
              planId = 1;
            }
          } else {
            // plan_id is missing, default to 1
            console.warn(`Missing plan_id for ${email}, defaulting to 1`);
            planId = 1;
          }

          // Ensure plan_id is a valid number (not NaN, not null, not undefined)
          if (isNaN(planId) || planId === null || planId === undefined) {
            console.error(`ERROR: plan_id is invalid for ${email}, forcing to 1`);
            planId = 1;
          }

          // plan_pricing_id behavior:
          // - If caller explicitly passes plan_pricing_id: null, KEEP it as null and include it in payload.
          // - If caller passes a number/string, coerce to number.
          // - If caller omits it, leave it undefined (do not include).
          const hasPlanPricingField = Object.prototype.hasOwnProperty.call(i, 'plan_pricing_id');
          let planPricingId; // undefined by default (means "omit field")
          if (hasPlanPricingField) {
            if (i.plan_pricing_id === null) {
              planPricingId = null;
            } else if (i.plan_pricing_id !== undefined) {
              if (Number.isFinite(i.plan_pricing_id)) {
                planPricingId = Number(i.plan_pricing_id);
              } else if (
                typeof i.plan_pricing_id === 'string' &&
                i.plan_pricing_id.trim() !== '' &&
                !Number.isNaN(Number(i.plan_pricing_id))
              ) {
                planPricingId = Number(i.plan_pricing_id);
              }
            }
          }

          const recipient = { email, plan_id: planId };

          // Include plan_pricing_id:
          // - include null if explicitly passed
          // - include numeric value if provided
          if (planPricingId === null) {
            recipient.plan_pricing_id = null;
          } else if (planPricingId !== undefined && !isNaN(planPricingId)) {
            recipient.plan_pricing_id = planPricingId;
          }

          const name = typeof i.name === 'string' ? i.name.trim() : '';
          if (name) recipient.name = name;
          const firstName = typeof i.first_name === 'string' ? i.first_name.trim() : '';
          const lastName = typeof i.last_name === 'string' ? i.last_name.trim() : '';
          if (firstName) recipient.first_name = firstName;
          if (lastName) recipient.last_name = lastName;
          if (i.i_will_pay !== null && i.i_will_pay !== undefined) {
            recipient.i_will_pay = Number(i.i_will_pay) === 1 ? 1 : 0;
          }

          // Final validation - ensure plan_id is included
          if (recipient.plan_id === undefined || recipient.plan_id === null || isNaN(recipient.plan_id)) {
            console.error(`CRITICAL: plan_id is still missing/invalid after normalization for ${email}:`, recipient);
            recipient.plan_id = 1; // Force it
          }

          return recipient;
        }

        return null;
      })
      .filter(Boolean);

    // Ensure we have at least one valid recipient
    if (normalized.length === 0) {
      const emptyErr = new Error('No valid email recipients provided. Please add at least one email address.');
      emptyErr.status = 400;
      emptyErr.statusCode = 400;
      throw emptyErr;
    }

    // Final validation: Ensure ALL recipients have valid plan_id
    const validatedRecipients = normalized.map(r => {
      if (r.plan_id === null || r.plan_id === undefined || isNaN(r.plan_id)) {
        console.warn(`Recipient ${r.email} has invalid plan_id: ${r.plan_id}, defaulting to 1`);
        return { ...r, plan_id: 1 };
      }
      return r;
    });

    const missingPlanId = validatedRecipients.filter(r =>
      r.plan_id === null ||
      r.plan_id === undefined ||
      isNaN(r.plan_id) ||
      typeof r.plan_id !== 'number'
    );

    if (missingPlanId.length > 0) {
      console.error('ERROR: Some recipients still missing plan_id after validation:', missingPlanId);
      const error = new Error(`Missing plan_id for ${missingPlanId.length} recipient(s). All recipients must have a plan_id.`);
      error.status = 400;
      error.statusCode = 400;
      throw error;
    }

    const payload = { selected_email_recipients: validatedRecipients };
    const body = JSON.stringify(payload);

    // Final check: Parse the JSON to ensure plan_id is included
    try {
      const parsed = JSON.parse(body);
      const stillMissing = parsed.selected_email_recipients?.filter(r =>
        !r.plan_id || r.plan_id === null || r.plan_id === undefined || isNaN(r.plan_id)
      );
      if (stillMissing && stillMissing.length > 0) {
        console.error('CRITICAL: plan_id missing after JSON.stringify:', stillMissing);
        console.error('Original payload:', payload);
        console.error('Stringified body:', body);
      }
    } catch (e) {
      console.error('Error parsing JSON body:', e);
    }

    // Always log the payload being sent (not just in development)
    console.log('=== Sending Invite Emails Request ===');
    console.log('URL:', url);
    console.log('Avatar ID:', avatarId);
    console.log('Final Payload:', JSON.stringify(payload, null, 2));
    console.log('Request Body (JSON string):', body);
    console.log('Recipients:', payload.selected_email_recipients.map(r => ({
      email: r.email,
      plan_id: r.plan_id,
      plan_id_type: typeof r.plan_id,
      plan_pricing_id: r.plan_pricing_id,
      plan_pricing_id_type: typeof r.plan_pricing_id
    })));

    // Validate payload structure matches expected format
    // plan_id is REQUIRED and must be a number (backend validation)
    // plan_pricing_id is optional but should be a number if provided
    const isValidFormat = Array.isArray(payload.selected_email_recipients) &&
      payload.selected_email_recipients.every(r =>
        typeof r === 'object' &&
        typeof r.email === 'string' &&
        typeof r.plan_id === 'number' &&
        !isNaN(r.plan_id) &&
        (r.plan_pricing_id === undefined || r.plan_pricing_id === null || (typeof r.plan_pricing_id === 'number' && !isNaN(r.plan_pricing_id)))
      );

    if (!isValidFormat) {
      console.error('❌ Invalid payload format detected:', payload);
      console.error('Recipients with issues:', payload.selected_email_recipients.map(r => ({
        email: r.email,
        hasPlanId: r.plan_id !== undefined && r.plan_id !== null,
        planId: r.plan_id,
        planIdType: typeof r.plan_id,
        isNaN: isNaN(r.plan_id)
      })));
    } else {
      console.log('✓ Payload format is valid - all recipients have plan_id');
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body,
      });

      let data;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          const text = await response.text();
          if (text) {
            try {
              data = JSON.parse(text);
            } catch {
              data = { error: text || `HTTP ${response.status}: ${response.statusText}` };
            }
          } else {
            data = { error: `HTTP ${response.status}: ${response.statusText}` };
          }
        }
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        data = { error: `HTTP ${response.status}: ${response.statusText}` };
      }

      if (!response.ok) {
        // Always log detailed error information (not just in development)
        console.error('=== Invite emails API error ===');
        console.error('Status:', response.status, response.statusText);
        console.error('Response data:', JSON.stringify(data, null, 2));
        console.error('Request payload:', JSON.stringify(payload, null, 2));
        console.error('Request body:', body);
        console.error('Request URL:', url);
        console.error('Avatar ID:', avatarId);
        console.error('Response headers:', Object.fromEntries(response.headers.entries()));

        let errorMessage = data.error?.msg || data.error?.error || data.msg || data.error || data.message || 'Failed to send invite emails';

        // Handle validation errors with details
        if (response.status === 422 && data.detail) {
          if (Array.isArray(data.detail)) {
            const validationErrors = data.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
            errorMessage = validationErrors || errorMessage;
          } else if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          }
        } else if (response.status === 400) {
          // Enhanced 400 error handling - log full response
          console.error('=== 400 Bad Request Error Details ===');
          console.error('Full response data:', JSON.stringify(data, null, 2));
          console.error('Request payload sent:', JSON.stringify(payload, null, 2));
          console.error('Request body string:', body);
          console.error('Request URL:', url);
          console.error('Avatar ID:', avatarId);

          // Try to extract error message from various possible locations
          errorMessage =
            data.error?.msg ||
            data.error?.error ||
            data.error?.message ||
            data.msg ||
            data.error ||
            data.message ||
            data.detail ||
            (typeof data.detail === 'string' ? data.detail : null) ||
            (Array.isArray(data.detail) ? data.detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ') : null) ||
            'Invalid request data. Please check your input and try again.';

          console.error('Extracted error message:', errorMessage);
        } else if (response.status === 401) {
          // Handle 401 Unauthorized - token might be expired, invalid, or wrong type
          const errorDetail = data.error?.message || data.error?.code || data.msg || data.message || data.error || 'Unauthorized';
          if (errorDetail && (errorDetail.toLowerCase().includes('token') || errorDetail.toLowerCase().includes('invalid token'))) {
            // Clear invalid token to force re-login
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            errorMessage = 'Your session has expired or token is invalid. Please sign in again.';
          } else {
            errorMessage = errorDetail || 'Unauthorized. Please sign in again.';
          }
        } else if (response.status === 403) {
          errorMessage = data.msg || data.error || data.message || 'Access forbidden. You don\'t have permission to perform this action.';
        } else if (response.status === 404) {
          errorMessage = data.msg || data.error || data.message || 'Avatar not found.';
        } else if (response.status >= 500) {
          // For 500 errors, show more details if available
          const serverErrorDetails = data.detail || data.error || data.message || data.msg || data.error?.msg || data.error?.error;
          if (serverErrorDetails) {
            errorMessage = `Server error: ${serverErrorDetails}`;
          } else {
            errorMessage = `Server error (${response.status}). Please check the browser console for details or try again later.`;
          }
        }

        const apiError = new Error(errorMessage);
        apiError.status = response.status;
        apiError.statusCode = response.status;
        apiError.response = data;
        apiError.request = { url, avatarId, payload };
        throw apiError;
      }

      return data;
    } catch (error) {
      if (error.status || error.statusCode) {
        throw error;
      }

      // Handle network errors
      if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
        throw new Error(`Network Error: Unable to connect to the API server. Please check your connection and try again.`);
      }

      throw error;
    }
  },

  // Get avatar plan details
  // curl -X GET "http://192.168.1.5:8090/api/avatars/plan" -H "Authorization: Bearer <ACCESS_TOKEN>"
  getAvatarPlan: () => apiRequest('/api/avatars/plan', {
    method: 'GET',
    headers: {
      accept: 'application/json',
    },
  }),

  // Get upgrade plan options
  getUpgradePlans: () => apiRequest('/api/avatars/upgrade-plan', {
    method: 'GET',
    headers: {
      accept: 'application/json',
    },
  }),


};

// Projects APIs
export const projectsAPI = {
  // Get projects list / details
  // curl -X 'GET' 'http://<host>:8090/api/projects?project_id=1&member_id=2' -H 'accept: application/json'
  getProjects: ({ project_id, member_id } = {}) => {
    const params = new URLSearchParams();
    if (project_id !== undefined && project_id !== null && `${project_id}`.length > 0) {
      params.set('project_id', project_id);
    }
    if (member_id !== undefined && member_id !== null && `${member_id}`.length > 0) {
      params.set('member_id', member_id);
    }
    const qs = params.toString();
    return apiRequest(`/api/projects${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: { accept: 'application/json' },
    });
  },

  // Get all projects for current user (as creator or member)
  // curl -X 'GET' 'http://<host>:8090/api/projects/my-projects' -H 'accept: application/json' -H 'Authorization: Bearer <ACCESS_TOKEN>'
  getMyProjects: () => apiRequest('/api/projects/my-projects', {
    method: 'GET',
    headers: { accept: 'application/json' },
  }),

  // Create project
  // curl -X 'POST' 'http://<host>:8090/api/projects' -H 'accept: application/json' -H 'Content-Type: application/json'
  // -d '{ "name": "test", "created_user": 1, "chat_type": "open", "member_ids": [] }'
  createProject: ({ name, created_user, chat_type, member_ids }) => apiRequest('/api/projects', {
    method: 'POST',
    headers: {
      accept: 'application/json',
    },
    body: JSON.stringify({ name, created_user, chat_type, member_ids }),
  }),

  // Add member to project
  // curl -X 'POST' 'https://sayagainservices.sayagain.ai/api/projects/members'
  // -H 'accept: application/json' -H 'Authorization: Bearer <ACCESS_TOKEN>'
  // -H 'Content-Type: application/json' -d '{ "project_id": 21, "user_id": 174 }'
  addMember: ({ project_id, user_id }) => apiRequest('/api/projects/members', {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ project_id, user_id }),
  }),

  // Get invitee members for a user (invite_id optional - pass when available from invite flow)
  // curl -X 'GET' 'https://sayagainservices.sayagain.ai/api/projects/invitee-members?user_id=167'
  // -H 'accept: application/json' -H 'Authorization: Bearer <ACCESS_TOKEN>'
  getInviteeMembers: (user_id, invite_id) => {
    let url = `/api/projects/invitee-members?user_id=${user_id}`;
    if (invite_id != null && invite_id !== '') {
      url += `&invite_id=${encodeURIComponent(invite_id)}`;
    }
    return apiRequest(url, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    });
  },

  // Get project members
  // curl -X 'GET' 'https://sayagainservices.sayagain.ai/api/projects/{project_id}/members'
  // -H 'accept: application/json' -H 'Authorization: Bearer <ACCESS_TOKEN>'
  getProjectMembers: (project_id) => apiRequest(`/api/projects/${project_id}/members`, {
    method: 'GET',
    headers: {
      accept: 'application/json',
    },
  }),

  // Get all users who share a Family Group with the current user
  getGroupMembers: () => apiRequest('/api/projects/group-members', {
    method: 'GET',
    headers: { accept: 'application/json' },
  }),

  // Rename group project (PUT /api/projects/{project_id} with body { name })
  updateProject: (project_id, { name }) => apiRequest(`/api/projects/${project_id}`, {
    method: 'PUT',
    headers: { accept: 'application/json' },
    body: JSON.stringify({ name }),
  }),

  // Delete group project (creator only)
  // DELETE /api/projects/{project_id}
  deleteProject: (project_id) => apiRequest(`/api/projects/${project_id}`, {
    method: 'DELETE',
    headers: { accept: 'application/json' },
  }),

  // Leave group project (member or owner; owner transfers or deletes if last)
  // DELETE /api/projects/{project_id}/leave
  leaveProject: (project_id) => apiRequest(`/api/projects/${project_id}/leave`, {
    method: 'DELETE',
    headers: { accept: 'application/json' },
  }),

  // POST /api/projects/send-invitation
  //
  // Multi-invite + email (backend requires i_will_pay === 1 to send email):
  //   { invite_emails: string[], plan_id, plan_pricing_id: null, i_will_pay: 1 }
  //
  // Single recipient (legacy):
  //   { invite_email, plan_id, plan_pricing_id, i_will_pay }  (0 = they pay, 1 = I pay)
  sendInvitation: (payload) => {
    const isBulk = Array.isArray(payload?.invite_emails);
    const body = isBulk
      ? {
          invite_emails: (payload.invite_emails || [])
            .map((e) => String(e || '').trim())
            .filter(Boolean),
          plan_id: payload.plan_id,
          plan_pricing_id: payload.plan_pricing_id ?? null,
          // Emails are only sent when i_will_pay is 1; default for bulk is "I pay".
          i_will_pay: payload.i_will_pay !== undefined && payload.i_will_pay !== null
            ? Number(payload.i_will_pay)
            : 1,
          ...(payload.avatar_id != null && payload.avatar_id !== ''
            ? { avatar_id: Number(payload.avatar_id) }
            : {}),
          ...(payload.invite_names && typeof payload.invite_names === 'object'
            ? { invite_names: payload.invite_names }
            : {}),
        }
      : {
          invite_email: payload.invite_email,
          plan_id: payload.plan_id,
          plan_pricing_id: payload.plan_pricing_id ?? null,
          ...(payload.i_will_pay !== undefined && payload.i_will_pay !== null
            ? { i_will_pay: Number(payload.i_will_pay) }
            : {}),
          ...(payload.avatar_id != null && payload.avatar_id !== ''
            ? { avatar_id: Number(payload.avatar_id) }
            : {}),
        };

    if (isBulk && (!body.invite_emails || body.invite_emails.length === 0)) {
      return Promise.reject(new Error('invite_emails must contain at least one email address.'));
    }
    if (!isBulk && !(body.invite_email && String(body.invite_email).trim())) {
      return Promise.reject(new Error('invite_email is required.'));
    }

    return apiRequest('/api/projects/send-invitation', {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  },
};

// Payments & invoices APIs (PayPal sandbox flow)
export const paymentsAPI = {
  // Backend contract: POST /api/payments/paypal/create-order
  // body: { avatar_plan_id, plan_interval, quantity?, total_usd?, amount_usd?, gift_line_items? }
  // gift_line_items: [{ interval, quantity, unit_amount_usd }] for mixed monthly+yearly bundles.
  createPaypalOrder: ({
    avatar_plan_id,
    plan_interval,
    first_month_discount,
    annual_discount,
    quantity,
    total_usd,
    amount_usd,
    gift_line_items,
  }) => {
    const t =
      total_usd != null && Number.isFinite(Number(total_usd))
        ? Number(total_usd)
        : amount_usd != null && Number.isFinite(Number(amount_usd))
          ? Number(amount_usd)
          : null;
    return apiRequest('/api/payments/paypal/create-order', {
      method: 'POST',
      headers: {
        accept: 'application/json',
      },
      body: JSON.stringify({
        avatar_plan_id,
        plan_interval,
        first_month_discount: first_month_discount || false,
        annual_discount: annual_discount || false,
        ...(quantity != null ? { quantity: Number(quantity) } : {}),
        ...(t != null
          ? {
              total_usd: t,
              amount_usd: t,
              total_amount_usd: t,
              bundle_total_usd: t,
              currency_code: 'USD',
              value: t.toFixed(2),
            }
          : {}),
        ...(Array.isArray(gift_line_items) && gift_line_items.length > 0 ? { gift_line_items } : {}),
      }),
    });
  },

  // Backend contract: POST /api/payments/paypal/capture
  capturePaypalOrder: ({
    order_id,
    avatar_plan_id,
    plan_interval,
    auto_renew,
    first_month_discount,
    annual_discount,
    quantity,
    total_usd,
    amount_usd,
    gift_line_items,
  }) => {
    const t =
      total_usd != null && Number.isFinite(Number(total_usd))
        ? Number(total_usd)
        : amount_usd != null && Number.isFinite(Number(amount_usd))
          ? Number(amount_usd)
          : null;
    return apiRequest('/api/payments/paypal/capture', {
      method: 'POST',
      headers: {
        accept: 'application/json',
      },
      body: JSON.stringify({
        order_id,
        avatar_plan_id,
        plan_interval,
        auto_renew,
        first_month_discount: first_month_discount || false,
        annual_discount: annual_discount || false,
        ...(quantity != null ? { quantity: Number(quantity) } : {}),
        ...(t != null
          ? {
              total_usd: t,
              amount_usd: t,
              total_amount_usd: t,
              bundle_total_usd: t,
              currency_code: 'USD',
              value: t.toFixed(2),
            }
          : {}),
        ...(Array.isArray(gift_line_items) && gift_line_items.length > 0 ? { gift_line_items } : {}),
      }),
    });
  },

  // GET /api/invoices/{invoice_id}/download
  // Returns Blob so caller can trigger file download.
  downloadInvoice: async (invoiceId) => {
    const cleanEndpoint = `/api/invoices/${invoiceId}/download`;
    const cleanBaseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    const url = shouldUseDevProxy() ? cleanEndpoint : `${cleanBaseUrl}${cleanEndpoint}`;
    const token = localStorage.getItem('access_token');
    const headers = { accept: 'application/pdf' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      let errMessage = `Failed to download invoice (${response.status})`;
      try {
        const data = await response.json();
        errMessage = data?.msg || data?.error || data?.message || errMessage;
      } catch (_) { }
      const error = new Error(errMessage);
      error.status = response.status;
      throw error;
    }

    return response.blob();
  },

  // GET /api/invoices/by-paypal-order?paypal_order_id=ORDER_ID
  getInvoiceByPaypalOrder: (paypal_order_id) => apiRequest(`/api/invoices/by-paypal-order?paypal_order_id=${encodeURIComponent(paypal_order_id)}`, {
    method: 'GET',
    headers: {
      accept: 'application/json',
    },
  }),
};

// Admin APIs
export const adminAPI = {
  getDashboardSummary: (adminToken) => {
    const headers = {};
    const token = adminToken ||
      localStorage.getItem('ADMIN_ACCESS_TOKEN') ||
      localStorage.getItem('admin_access_token') ||
      localStorage.getItem('access_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return apiRequest('/api/admin/dashboard/summary', {
      method: 'GET',
      headers,
    });
  },

  getAdminPlans: (page = 1, pageSize = 10, adminToken) => {
    const headers = {};
    const token = adminToken ||
      localStorage.getItem('ADMIN_ACCESS_TOKEN') ||
      localStorage.getItem('admin_access_token') ||
      localStorage.getItem('access_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return apiRequest(`/api/admin/plans?page=${page}&page_size=${pageSize}`, {
      method: 'GET',
      headers,
    });
  },
};


// Contact APIs
export const contactAPI = {
  submitContactUs: (data) => apiRequest('/api/contact-us', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Beta APIs
export const betaAPI = {
  getStatus: () => apiRequest('/api/beta/status', { method: 'GET' }),
  register: (data) => apiRequest('/api/beta/register', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

export default {
  auth: authAPI,
  plans: plansAPI,
  users: usersAPI,
  avatars: avatarsAPI,
  payments: paymentsAPI,
  admin: adminAPI,
  contact: contactAPI,
};

